import React, { useMemo, useState } from 'react';
import {
  AdminHeaderAction,
  adminFormInputClasses,
  adminFormSelectClasses,
  adminFormTextareaClasses,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  DATA_REQUEST_FORM_IDS,
  EXPORT_FORMAT_OPTIONS,
  EXPORT_SCOPE_OPTIONS,
} from './settingsDefaults';
import {
  SettingsConfirmModal,
  SettingsField,
  SettingsReadOnlyRow,
  SettingsSectionShell,
  SettingsSuccessToast,
} from './SettingsSectionShell';

const SUBSECTIONS = [
  { id: 'export', label: 'Account Export Request' },
  { id: 'anonymisation', label: 'Account Anonymisation Request' },
  { id: 'restore', label: 'Restore Request' },
];

const SAMPLE_ACCOUNTS = [
  { id: 'CUS-100284', type: 'Customer', label: 'Jordan Lee — CUS-100284', state: 'Active', eligible: true },
  { id: 'CUS-100391', type: 'Customer', label: 'Sam Patel — CUS-100391', state: 'Closed (retention)', eligible: true },
  { id: 'BUS-220184', type: 'Business', label: 'Northside Plumbing — BUS-220184', state: 'Active', eligible: true },
  { id: 'BUS-220401', type: 'Business', label: 'Coastal Electrical — BUS-220401', state: 'Closed (retention)', eligible: true },
];

const SAMPLE_RESTORE_TARGETS = [
  {
    id: 'CUS-100391',
    type: 'Customer Account',
    label: 'Sam Patel — CUS-100391',
    lifecycleState: 'Closed — within 120-day retention',
    restoreAvailability: 'Eligible',
  },
  {
    id: 'BUS-220401',
    type: 'Business Account',
    label: 'Coastal Electrical — BUS-220401',
    lifecycleState: 'Closed — within 120-day retention',
    restoreAvailability: 'Eligible',
  },
  {
    id: 'REC-884201',
    type: 'Eligible Retained Record',
    label: 'Retained quote package — REC-884201',
    lifecycleState: 'Retained',
    restoreAvailability: 'Eligible',
  },
];

const emptyExport = {
  accountType: 'Customer',
  accountId: '',
  exportScope: 'All Available Data',
  exportFormat: 'CSV (ZIP)',
  supportingReference: '',
  reason: '',
};

const emptyAnonymisation = {
  accountType: 'Customer',
  accountId: '',
  supportingReference: '',
  reason: '',
};

const emptyRestore = {
  restoreTargetType: 'Customer Account',
  targetId: '',
  supportingReference: '',
  reason: '',
};

const DataRecoveryPanel = () => {
  const [activeId, setActiveId] = useState('export');
  const [exportForm, setExportForm] = useState(emptyExport);
  const [anonForm, setAnonForm] = useState(emptyAnonymisation);
  const [restoreForm, setRestoreForm] = useState(emptyRestore);
  const [errors, setErrors] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [accountQuery, setAccountQuery] = useState('');

  const selectedAnonAccount = SAMPLE_ACCOUNTS.find((a) => a.id === anonForm.accountId);
  const selectedRestoreTarget = SAMPLE_RESTORE_TARGETS.find((t) => t.id === restoreForm.targetId);

  const filteredAccounts = useMemo(() => {
    const type = activeId === 'export' ? exportForm.accountType : anonForm.accountType;
    const q = accountQuery.trim().toLowerCase();
    return SAMPLE_ACCOUNTS.filter(
      (a) =>
        a.type === type &&
        (!q || a.label.toLowerCase().includes(q) || a.id.toLowerCase().includes(q))
    );
  }, [activeId, exportForm.accountType, anonForm.accountType, accountQuery]);

  const filteredRestore = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    return SAMPLE_RESTORE_TARGETS.filter(
      (t) =>
        t.type === restoreForm.restoreTargetType &&
        (!q || t.label.toLowerCase().includes(q) || t.id.toLowerCase().includes(q))
    );
  }, [restoreForm.restoreTargetType, accountQuery]);

  const nextRequestId = () => `REQ-${Math.floor(100000 + Math.random() * 900000)}`;

  const validate = () => {
    const next = {};
    if (activeId === 'export') {
      if (!exportForm.accountId) next.accountId = 'Select an eligible account.';
      if (!exportForm.exportScope) next.exportScope = 'Export Scope is required.';
      if (!exportForm.exportFormat) next.exportFormat = 'Export Format is required.';
      if (!exportForm.reason?.trim()) next.reason = 'Reason is required.';
    }
    if (activeId === 'anonymisation') {
      if (!anonForm.accountId) next.accountId = 'Select an eligible account.';
      if (!anonForm.reason?.trim()) next.reason = 'Reason is required.';
      if (selectedAnonAccount && !selectedAnonAccount.eligible) {
        next.accountId = 'This record is no longer available.';
      }
    }
    if (activeId === 'restore') {
      if (!restoreForm.targetId) next.targetId = 'Select an eligible account.';
      if (!restoreForm.reason?.trim()) next.reason = 'Reason is required.';
      if (selectedRestoreTarget && selectedRestoreTarget.restoreAvailability !== 'Eligible') {
        next.targetId = 'This record is no longer available.';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const openConfirm = () => {
    if (!validate()) return;
    if (activeId === 'export') {
      setConfirm({
        title: 'Create Account Export Request?',
        message: 'This creates a protected request to prepare available account data.',
        successPrefix: 'Export request created',
      });
    } else if (activeId === 'anonymisation') {
      setConfirm({
        title: 'Create Account Anonymisation Request?',
        message: 'This creates a protected request that may permanently change eligible identity information.',
        successPrefix: 'Anonymisation request created',
      });
    } else {
      setConfirm({
        title: 'Create Restore Request?',
        message: 'This creates a protected request to restore an eligible retained account or record.',
        successPrefix: 'Restore request created',
      });
    }
  };

  const submitRequest = async () => {
    if (submitting) return;
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 400));
    const requestId = nextRequestId();
    setSuccessMessage(`${confirm.successPrefix} — ${requestId}.`);
    setConfirm(null);
    setSubmitting(false);
    if (activeId === 'export') setExportForm(emptyExport);
    if (activeId === 'anonymisation') setAnonForm(emptyAnonymisation);
    if (activeId === 'restore') setRestoreForm(emptyRestore);
    setAccountQuery('');
    setErrors({});
  };

  const body = {
    export: (
      <>
        <SettingsReadOnlyRow
          label="Form"
          value={`${DATA_REQUEST_FORM_IDS.accountExport} — Account Export Request Form`}
          mono
        />
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Account Type" required>
            <select
              value={exportForm.accountType}
              onChange={(e) => {
                setExportForm((p) => ({
                  ...p,
                  accountType: e.target.value,
                  accountId: '',
                  exportScope: 'All Available Data',
                  exportFormat: 'CSV (ZIP)',
                }));
                setAccountQuery('');
              }}
              className={adminFormSelectClasses()}
            >
              <option>Customer</option>
              <option>Business</option>
            </select>
          </SettingsField>
          <SettingsField label="Account" required error={errors.accountId}>
            <input
              type="search"
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
              placeholder="Search account"
              className={`w-full mb-2 ${adminFormInputClasses(false)}`}
            />
            <select
              value={exportForm.accountId}
              onChange={(e) => setExportForm((p) => ({ ...p, accountId: e.target.value }))}
              className={adminFormSelectClasses()}
            >
              <option value="">Select account</option>
              {filteredAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsReadOnlyRow label="Permanent Account ID" value={exportForm.accountId || '—'} mono />
          <SettingsField label="Export Scope" required error={errors.exportScope}>
            <select
              value={exportForm.exportScope}
              onChange={(e) => setExportForm((p) => ({ ...p, exportScope: e.target.value }))}
              className={adminFormSelectClasses()}
            >
              {EXPORT_SCOPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Export Format" required error={errors.exportFormat}>
            <select
              value={exportForm.exportFormat}
              onChange={(e) => setExportForm((p) => ({ ...p, exportFormat: e.target.value }))}
              className={adminFormSelectClasses()}
            >
              {EXPORT_FORMAT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Supporting Case or Request Reference">
            <input
              type="text"
              value={exportForm.supportingReference}
              onChange={(e) => setExportForm((p) => ({ ...p, supportingReference: e.target.value }))}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
        </div>
        <SettingsField label="Reason" required error={errors.reason}>
          <textarea
            rows={3}
            value={exportForm.reason}
            onChange={(e) => setExportForm((p) => ({ ...p, reason: e.target.value }))}
            className={adminFormTextareaClasses()}
          />
        </SettingsField>
        <div className="flex justify-end">
          <AdminHeaderAction type="button" onClick={openConfirm} disabled={submitting}>
            Create Request
          </AdminHeaderAction>
        </div>
      </>
    ),
    anonymisation: (
      <>
        <SettingsReadOnlyRow
          label="Form"
          value={`${DATA_REQUEST_FORM_IDS.accountAnonymisation} — Account Anonymisation Request Form`}
          mono
        />
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Account Type" required>
            <select
              value={anonForm.accountType}
              onChange={(e) => {
                setAnonForm((p) => ({ ...p, accountType: e.target.value, accountId: '' }));
                setAccountQuery('');
              }}
              className={adminFormSelectClasses()}
            >
              <option>Customer</option>
              <option>Business</option>
            </select>
          </SettingsField>
          <SettingsField label="Account" required error={errors.accountId}>
            <input
              type="search"
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
              placeholder="Search account"
              className={`w-full mb-2 ${adminFormInputClasses(false)}`}
            />
            <select
              value={anonForm.accountId}
              onChange={(e) => setAnonForm((p) => ({ ...p, accountId: e.target.value }))}
              className={adminFormSelectClasses()}
            >
              <option value="">Select account</option>
              {filteredAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsReadOnlyRow label="Permanent Account ID" value={anonForm.accountId || '—'} mono />
          <SettingsReadOnlyRow label="Current Account State" value={selectedAnonAccount?.state || '—'} />
          <SettingsReadOnlyRow
            label="Eligibility Check"
            value={
              selectedAnonAccount
                ? selectedAnonAccount.eligible
                  ? 'Eligible'
                  : 'Not eligible'
                : '—'
            }
          />
          <SettingsField label="Supporting Case or Request Reference">
            <input
              type="text"
              value={anonForm.supportingReference}
              onChange={(e) => setAnonForm((p) => ({ ...p, supportingReference: e.target.value }))}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
        </div>
        <SettingsField label="Reason" required error={errors.reason}>
          <textarea
            rows={3}
            value={anonForm.reason}
            onChange={(e) => setAnonForm((p) => ({ ...p, reason: e.target.value }))}
            className={adminFormTextareaClasses()}
          />
        </SettingsField>
        <div className="flex justify-end">
          <AdminHeaderAction type="button" onClick={openConfirm} disabled={submitting}>
            Create Request
          </AdminHeaderAction>
        </div>
      </>
    ),
    restore: (
      <>
        <SettingsReadOnlyRow
          label="Form"
          value={`${DATA_REQUEST_FORM_IDS.restore} — Restore Request Form`}
          mono
        />
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Restore Target Type" required>
            <select
              value={restoreForm.restoreTargetType}
              onChange={(e) => {
                setRestoreForm((p) => ({ ...p, restoreTargetType: e.target.value, targetId: '' }));
                setAccountQuery('');
              }}
              className={adminFormSelectClasses()}
            >
              <option>Customer Account</option>
              <option>Business Account</option>
              <option>Eligible Retained Record</option>
            </select>
          </SettingsField>
          <SettingsField label="Target" required error={errors.targetId}>
            <input
              type="search"
              value={accountQuery}
              onChange={(e) => setAccountQuery(e.target.value)}
              placeholder="Search target"
              className={`w-full mb-2 ${adminFormInputClasses(false)}`}
            />
            <select
              value={restoreForm.targetId}
              onChange={(e) => setRestoreForm((p) => ({ ...p, targetId: e.target.value }))}
              className={adminFormSelectClasses()}
            >
              <option value="">Select target</option>
              {filteredRestore.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsReadOnlyRow label="Permanent Target ID" value={restoreForm.targetId || '—'} mono />
          <SettingsReadOnlyRow
            label="Current Lifecycle State"
            value={selectedRestoreTarget?.lifecycleState || '—'}
          />
          <SettingsReadOnlyRow
            label="Restore Availability"
            value={selectedRestoreTarget?.restoreAvailability || '—'}
          />
          <SettingsField label="Supporting Case or Request Reference">
            <input
              type="text"
              value={restoreForm.supportingReference}
              onChange={(e) => setRestoreForm((p) => ({ ...p, supportingReference: e.target.value }))}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
        </div>
        <SettingsField label="Reason" required error={errors.reason}>
          <textarea
            rows={3}
            value={restoreForm.reason}
            onChange={(e) => setRestoreForm((p) => ({ ...p, reason: e.target.value }))}
            className={adminFormTextareaClasses()}
          />
        </SettingsField>
        <div className="flex justify-end">
          <AdminHeaderAction type="button" onClick={openConfirm} disabled={submitting}>
            Create Request
          </AdminHeaderAction>
        </div>
      </>
    ),
  }[activeId];

  return (
    <>
      <SettingsSectionShell
        subsections={SUBSECTIONS}
        activeId={activeId}
        onSelectSubsection={(id) => {
          setActiveId(id);
          setAccountQuery('');
          setErrors({});
        }}
        hasChanges={false}
        showFooter={false}
      >
        {body}
      </SettingsSectionShell>

      {confirm ? (
        <SettingsConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel="Create Request"
          onCancel={() => setConfirm(null)}
          onConfirm={submitRequest}
          confirming={submitting}
        />
      ) : null}

      <SettingsSuccessToast message={successMessage} />
    </>
  );
};

export default DataRecoveryPanel;
